<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { ArrowDown, ArrowUp, Plus } from '@lucide/vue'
import { Badge, Button, ConfirmAction, EmptyState, FormField, Modal, PageHeader, Section, Select, Spinner, Table, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { createHomepagePopularPin, deleteHomepagePopularPin, listHomepagePopularPins, reorderHomepagePopularPins, updateHomepagePopularPin } from '../api/homepage-popular-pins.js'

const { t } = useI18n()
const toast = useToast()
const api = inject('api')
if (!api) throw new Error('HomepagePopularPins: api provider is required')
const items = ref([])
const packs = ref([])
const loading = ref(true)
const error = ref('')
const formOpen = ref(false)
const saving = ref(false)
const selectedReferenceId = ref('')
const columns = computed(() => [
  { key: 'sort_order', label: t('visualGameAdmin.homepagePopularPins.order'), width: '130px' },
  { key: 'story', label: t('visualGameAdmin.homepagePopularPins.story'), width: '520px', wrap: true },
  { key: 'enabled', label: t('visualGameAdmin.homepagePopularPins.status'), width: '100px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '220px', align: 'right' },
])
const storyOptions = computed(() => {
  const pinned = new Set(items.value.map(item => item.reference_id))
  const options = new Map()
  for (const pack of packs.value) {
    const value = pack.series_id ? `series:${pack.series_id}` : `scenario:${pack.id}`
    if (pinned.has(value)) continue
    const label = pack.series_id ? (pack.series_title || pack.title) : pack.title
    if (!options.has(value)) options.set(value, { value, label: `${label} · ${value}` })
  }
  return [...options.values()].sort((left, right) => left.label.localeCompare(right.label))
})

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [pinResult, packResult] = await Promise.all([listHomepagePopularPins(), api.visualGame.listPacks()])
    items.value = pinResult.items
    packs.value = packResult.packs
  } catch (requestError) { error.value = requestError.message }
  finally { loading.value = false }
}

function storyTitle(referenceId) {
  if (referenceId.startsWith('series:')) {
    const pack = packs.value.find(item => item.series_id === referenceId.slice('series:'.length))
    return pack?.series_title || pack?.title || referenceId
  }
  return packs.value.find(item => item.id === referenceId.slice('scenario:'.length))?.title || referenceId
}

async function add() {
  saving.value = true
  try {
    await createHomepagePopularPin({ reference_id: selectedReferenceId.value, enabled: true })
    formOpen.value = false
    selectedReferenceId.value = ''
    await load()
    toast.success(t('visualGameAdmin.homepagePopularPins.saved'))
  } catch (requestError) { toast.error(requestError.message, { duration: 0 }) }
  finally { saving.value = false }
}

async function toggle(item) {
  await updateHomepagePopularPin(item.id, { reference_id: item.reference_id, enabled: !item.enabled })
  await load()
}

async function move(index, offset) {
  const next = [...items.value]
  const [item] = next.splice(index, 1)
  next.splice(index + offset, 0, item)
  items.value = (await reorderHomepagePopularPins(next.map(entry => entry.id))).items
}

async function remove(item) { await deleteHomepagePopularPin(item.id) }
async function onDeleted() { await load(); toast.success(t('visualGameAdmin.homepagePopularPins.deleted')) }
</script>

<template>
  <div class="popular-pins-page vga-page">
    <PageHeader :title="t('visualGameAdmin.homepagePopularPins.title')" :subtitle="t('visualGameAdmin.homepagePopularPins.subtitle')"><template #actions><Button @click="formOpen = true"><Plus :size="16" />{{ t('visualGameAdmin.homepagePopularPins.add') }}</Button></template></PageHeader>
    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state"><p class="vga-error">{{ error }}</p><Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button></div>
    <Section v-else :padded="false">
      <EmptyState v-if="items.length === 0" :title="t('visualGameAdmin.homepagePopularPins.empty')" :description="t('visualGameAdmin.homepagePopularPins.emptyDescription')" />
      <Table v-else :columns="columns" :rows="items">
        <template #sort_order="{ row }"><div class="popular-pins-page__order"><Button variant="ghost" size="sm" :disabled="items.indexOf(row) === 0" :aria-label="t('visualGameAdmin.homepagePopularPins.moveUp')" @click="move(items.indexOf(row), -1)"><ArrowUp :size="14" /></Button><Button variant="ghost" size="sm" :disabled="items.indexOf(row) === items.length - 1" :aria-label="t('visualGameAdmin.homepagePopularPins.moveDown')" @click="move(items.indexOf(row), 1)"><ArrowDown :size="14" /></Button></div></template>
        <template #story="{ row }"><div class="popular-pins-page__story"><strong>{{ storyTitle(row.reference_id) }}</strong><small>{{ row.reference_id }}</small></div></template>
        <template #enabled="{ row }"><Badge size="sm" :variant="row.enabled ? 'success' : 'neutral'">{{ t(row.enabled ? 'visualGameAdmin.homepagePopularPins.enabled' : 'visualGameAdmin.homepagePopularPins.disabled') }}</Badge></template>
        <template #actions="{ row }"><div class="popular-pins-page__actions"><Button variant="ghost" size="sm" @click="toggle(row)">{{ t(row.enabled ? 'visualGameAdmin.homepagePopularPins.disable' : 'visualGameAdmin.homepagePopularPins.enable') }}</Button><ConfirmAction :label="t('visualGameAdmin.common.delete')" :confirm-text="t('visualGameAdmin.homepagePopularPins.deleteConfirm')" :cancel-label="t('visualGameAdmin.common.cancel')" :ok-label="t('visualGameAdmin.common.delete')" :pending-label="t('visualGameAdmin.common.working')" danger :action="() => remove(row)" @done="onDeleted" @error="requestError => toast.error(requestError.message, { duration: 0 })" /></div></template>
      </Table>
    </Section>
    <Modal :open="formOpen" :title="t('visualGameAdmin.homepagePopularPins.createTitle')" width="600px" :close-on-backdrop="!saving" @update:open="formOpen = $event">
      <FormField :label="t('visualGameAdmin.homepagePopularPins.story')" :hint="t('visualGameAdmin.homepagePopularPins.storyHint')" required><Select v-model="selectedReferenceId" :options="storyOptions" :placeholder="t('visualGameAdmin.homepagePopularPins.storyPlaceholder')" :search-placeholder="t('visualGameAdmin.homepagePopularPins.storySearchPlaceholder')" :empty-text="t('visualGameAdmin.homepagePopularPins.storySearchEmpty')" searchable block /></FormField>
      <template #footer><Button variant="ghost" :disabled="saving" @click="formOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button><Button :loading="saving" :disabled="!selectedReferenceId" @click="add">{{ t('visualGameAdmin.common.save') }}</Button></template>
    </Modal>
  </div>
</template>

<style scoped>
.popular-pins-page__order,.popular-pins-page__actions{display:flex;align-items:center;gap:var(--drama-space-xs)}.popular-pins-page__story{display:grid;gap:var(--drama-space-2xs)}.popular-pins-page__story small{color:var(--drama-color-text-muted)}
</style>
